import React, { useEffect, useMemo, useState } from "react";
import { Edit, MedalStar, DocumentUpload, FilterSearch } from "iconsax-react";
import * as FileSaver from 'file-saver';
import Excel from "exceljs";
import beveatApiResourceOwner from "../../../api/resource_owner_beveat_api.js";
import { commonConfigs, urls } from "../../../../config.js";
import UpdateCertificateDetailsModal from "../../components/update_certificate_details_modal.jsx";
import ConfirmCertificationModal from "./components/confirm_certification_modal.jsx";
import AdminCertificationDetailsTable from "./components/admin_certification_details_table.jsx";
import AdminCertificationFilter from "./components/admin_certification_filter.jsx";
import { saveAs } from 'file-saver';
import { CERTIFICATE_PROFICIENCY_SYSCODE } from "../../../constants/certificate_proficiency_syscode.jsx";
import { DEVELOPER_CERTIFICATION_STATUS } from "../../../constants/developer_certification_status.jsx";
import { TrainingProviderSyscodeString, TRAINING_PROVIDER_SYSCODE } from "../../../constants/training_provider_syscode.jsx";
import { CertificateSyscodeString, CERTIFICATE_SYSCODE, CertificateSyscodeMap, CertificateSyscodeByCoursework, CertificateSyscodeByCourseworkNetsOnly } from "../../../constants/certificate_syscode.jsx";
import UploadCertificateModal from "./components/upload_certificate_modal.jsx";
import { getUser } from "../../../services/local_service.js";
import { ACCESS_ROLE } from "../../../constants/access_roles.jsx";

const getEnvironmentSyscodes = (environment) => {
  const environmentKeyMap = { "Sandbox": "Sandbox", "UAT": "Staging", "Go Live": "Production" };
  return CertificateSyscodeMap[environmentKeyMap[environment]] || [];
};

const getCourseworkSyscodes = (coursework) => CertificateSyscodeByCoursework[coursework] || [];

const getCombinedSyscodes = (coursework, environment) => {
  let syscodes = [];
  if (coursework !== "All" && environment !== "All") {
    const courseworkSyscodes = getCourseworkSyscodes(coursework);
    const environmentSyscodes = getEnvironmentSyscodes(environment);
    syscodes = courseworkSyscodes.filter(code => environmentSyscodes.includes(code));
  } else if (coursework !== "All") {
    syscodes = getCourseworkSyscodes(coursework);
  } else if (environment !== "All") {
    syscodes = getEnvironmentSyscodes(environment);
  }
  return syscodes;
};

const AdminCertificationLayout = () => {
  const currentUser = getUser();
  const isCertificateAdmin = currentUser?.role_name === ACCESS_ROLE.CERTIFICATE_ADMIN;
  const isNetsAdmin = currentUser?.role_name === ACCESS_ROLE.NETS_ADMIN;
  const userTrainingProviderId = currentUser?.training_provider_id;
  const availableTabs = (isCertificateAdmin || isNetsAdmin) ? ["Released"] : ["Pending", "Certified", "Released"];
  const userTrainingProviderName = userTrainingProviderId ? TrainingProviderSyscodeString[userTrainingProviderId] : "All";

  const [load, setLoad] = useState(true);
  const [apiError, setApiError] = useState(null);
  const [activeTab, setActiveTab] = useState(availableTabs[0]);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [isMobile, setIsMobile] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [apiSearchTerm, setApiSearchTerm] = useState("");

  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;
  const firstDayOfMonth = `${currentYear}-${currentMonth.toString().padStart(2, '0')}-01`;
  const lastDayOfMonth = new Date(currentYear, currentMonth, 0);
  const lastDayOfMonthFormatted = `${currentYear}-${currentMonth.toString().padStart(2, '0')}-${lastDayOfMonth.getDate()}`;

  const [pendingPage, setPendingPage] = useState(0);
  const [certifiedPage, setCertifiedPage] = useState(0);
  const [releasedPage, setReleasedPage] = useState(0);
  const [totalPendingPages, setTotalPendingPages] = useState(0);
  const [totalCertifiedPages, setTotalCertifiedPages] = useState(0);
  const [totalReleasedPages, setTotalReleasedPages] = useState(0);
  const [totalPendingRecords, setTotalPendingRecords] = useState(0);
  const [totalCertifiedRecords, setTotalCertifiedRecords] = useState(0);
  const [totalReleasedRecords, setTotalReleasedRecords] = useState(0);
  const [pendingData, setPendingData] = useState([]);
  const [certifiedData, setCertifiedData] = useState([]);
  const [releasedData, setReleasedData] = useState([]);

  const [filters, setFilters] = useState({
    coursework: "All",
    environment: "All",
    school: userTrainingProviderName,
    startDate: firstDayOfMonth,
    endDate: lastDayOfMonthFormatted,
  });

  const [appliedFilters, setAppliedFilters] = useState({
    coursework: "All",
    environment: "All",
    school: userTrainingProviderName,
    startDate: firstDayOfMonth,
    endDate: lastDayOfMonthFormatted,
  });

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingCertificate, setEditingCertificate] = useState(null);
  const [editFormData, setEditFormData] = useState({
    name: "", email: "", proficiency: "", currentProficiency: "", orgInvoicePlanId: "",
    developerProjId: "", developerCertificateId: "", originalProficiency: ""
  });
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [certifyingItem, setCertifyingItem] = useState(null);
  const [selectedProficiency, setSelectedProficiency] = useState("");
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadingCertificate, setUploadingCertificate] = useState(null);

  const getCurrentPage = () => {
    switch (activeTab) {
      case "Pending": return pendingPage;
      case "Certified": return certifiedPage;
      case "Released": return releasedPage;
    }
  };

  const getCurrentData = () => {
    switch (activeTab) {
      case "Pending": return pendingData;
      case "Certified": return certifiedData;
      case "Released": return releasedData;
    }
  };

  const getTotalPages = () => {
    switch (activeTab) {
      case "Pending": return totalPendingPages;
      case "Certified": return totalCertifiedPages;
      case "Released": return totalReleasedPages;
    }
  };

  const getTotalRecords = () => {
    switch (activeTab) {
      case "Pending": return totalPendingRecords;
      case "Certified": return totalCertifiedRecords;
      case "Released": return totalReleasedRecords;
    }
  };

  // const handleEditCertificate = (certificate) => {
  //   setEditingCertificate(certificate);
  //   setEditFormData({
  //     name: certificate.name,
  //     email: certificate.email,
  //     proficiency: certificate._original?.certificate_proficiency_syscode || "",
  //     currentProficiency: certificate.proficiency || "",
  //     orgInvoicePlanId: certificate._original?.org_invoice_plan_id,
  //     developerProjId: certificate._original?.developer_proj_id,
  //     developerCertificateId: certificate._original?.developer_certificate_id,
  //     originalProficiency: certificate._original?.certificate_proficiency_syscode
  //   });
  //   setIsEditModalOpen(true);
  // };
  const handleEditCertificate = (certificate) => {
    const formattedData = {
      ...certificate,
      proficiency: certificate._original?.certificate_proficiency_syscode || "",
      currentProficiency: certificate.proficiency || "",
      orgInvoicePlanId: certificate._original?.org_invoice_plan_id,
      developerProjId: certificate._original?.developer_proj_id,
      developerCertificateId: certificate._original?.developer_certificate_id,
      originalProficiency: certificate._original?.certificate_proficiency_syscode
    };

    setEditingCertificate(formattedData);
    setIsEditModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsEditModalOpen(false);
    setEditingCertificate(null);
    setEditFormData({
      name: "", email: "", proficiency: "", currentProficiency: "", orgInvoicePlanId: "",
      developerProjId: "", developerCertificateId: "", originalProficiency: ""
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleOpenUploadModal = (certificate) => {
    setUploadingCertificate(certificate);
    setIsUploadModalOpen(true);
  };

  const handleCloseUploadModal = () => {
    setIsUploadModalOpen(false);
    setUploadingCertificate(null);
  };

  const handleCertify = (certificate) => {
    setCertifyingItem(certificate);
    setSelectedProficiency("");
    setIsConfirmModalOpen(true);
  };

  const handleProficiencyChange = (e) => {
    setSelectedProficiency(e.target.value);
  };

  const handleCancelCertification = () => {
    setIsConfirmModalOpen(false);
    setCertifyingItem(null);
    setSelectedProficiency("");
  };

  const handleUpdateSuccess = async () => {
    if (availableTabs.includes("Pending")) await getAllCertifications("Pending");
    if (availableTabs.includes("Certified")) await getAllCertifications("Certified");
    if (availableTabs.includes("Released")) await getAllCertifications("Released");
  };

  const handleConfirmSuccess = async () => {
    if (availableTabs.includes("Pending")) await getAllCertifications("Pending");
    if (availableTabs.includes("Certified")) await getAllCertifications("Certified");
    if (availableTabs.includes("Released")) await getAllCertifications("Released");
  };

  const handleUploadSuccess = async () => {
    if (availableTabs.includes("Pending")) await getAllCertifications("Pending");
    if (availableTabs.includes("Certified")) await getAllCertifications("Certified");
    if (availableTabs.includes("Released")) await getAllCertifications("Released");
  };

  const handlePageChange = (newPage) => {
    switch (activeTab) {
      case "Pending": setPendingPage(newPage); break;
      case "Certified": setCertifiedPage(newPage); break;
      case "Released": setReleasedPage(newPage); break;
    }
  };

  const handleAdvancedSearch = () => {
    setApiSearchTerm(searchTerm.trim());
    setPendingPage(0);
    setCertifiedPage(0);
    setReleasedPage(0);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') handleAdvancedSearch();
  };

  const getServerRenderedCertificate = async (developer_certificate_id) => {
    const certificateUrl = urls.resourceOwnerUrl + commonConfigs.apiUrls.getCertificatePdf(developer_certificate_id);
    try {
      const response = await beveatApiResourceOwner.get(certificateUrl, {
        headers: commonConfigs.resourceOwnerApiHeader,
        responseType: "arraybuffer",
        skipStatusValidation: true,
      });

      const disposition = response.headers["content-disposition"] || "";
      let serverFileName = null;
      if (disposition) {
        const match = disposition.match(/filename="([^"]+)"/i);
        if (match) serverFileName = match[1];
      }

      const mimeType = response.headers["content-type"] || "application/pdf";
      const blob = new Blob([response.data], { type: mimeType });
      return { blob, fileName: serverFileName };
    } catch (error) {
      throw new Error("Download failed. Certificate template not found");
    }
  };

  const getAllCertificationsForDownload = async () => {
    try {
      const endDate = appliedFilters.endDate || lastDayOfMonthFormatted;
      const startDate = appliedFilters.startDate || firstDayOfMonth;

      let statusCode;
      switch (activeTab) {
        case "Pending": statusCode = DEVELOPER_CERTIFICATION_STATUS.Pending; break;
        case "Certified": statusCode = DEVELOPER_CERTIFICATION_STATUS.Certified; break;
        case "Released": statusCode = DEVELOPER_CERTIFICATION_STATUS.Released; break;
        default: statusCode = DEVELOPER_CERTIFICATION_STATUS.Pending;
      }

      let trainingProvider = "";
      if (appliedFilters.school !== "All") {
        const trainingProviderKey = Object.keys(TrainingProviderSyscodeString).find(
          key => TrainingProviderSyscodeString[key] === appliedFilters.school
        );
        if (trainingProviderKey) trainingProvider = trainingProviderKey;
      }

      const combinedSyscodes = getCombinedSyscodes(appliedFilters.coursework, appliedFilters.environment);
      let certificateSyscode = combinedSyscodes.length > 0 ? combinedSyscodes.join(',') : "";

      const getAllDataUrl = urls.resourceOwnerUrl +
        `/developers/certifications?start_date=${startDate}&end_date=${endDate}&certificate_syscode=${certificateSyscode}&developer_certification_status=${statusCode}&training_provider_id=${trainingProvider}&search_term=${apiSearchTerm || ""}`;

      const response = await beveatApiResourceOwner.get(getAllDataUrl, {
        headers: commonConfigs.resourceOwnerApiHeader,
      });

      let certificatesArray = null;
      if (response.data?.result?.data?.certificates && Array.isArray(response.data.result.data.certificates)) {
        certificatesArray = response.data.result.data.certificates;
      } else if (response.data?.result?.data && Array.isArray(response.data.result.data)) {
        certificatesArray = response.data.result.data;
      }

      return certificatesArray && Array.isArray(certificatesArray) ? transformApiData(certificatesArray) : [];
    } catch (error) {
      console.error("Error fetching all data for download:", error);
      throw error;
    }
  };

  const handleDownloadCertificates = async () => {
    if (selectedRows.size === 0) return;

    const dataToDownload = getCurrentData().filter((item) => {
      const uniqueId = item._original?.developer_certificate_id || `${item.email}-${item.txn_id}`;
      return selectedRows.has(uniqueId);
    });

    if (dataToDownload.length === 0) {
      alert("No data available to download certificates");
      return;
    }

    const failedDownloads = [];
    let successCount = 0;

    for (let i = 0; i < dataToDownload.length; i++) {
      const cert = dataToDownload[i];
      const developer_certificate_id = cert._original?.developer_certificate_id;

      if (!developer_certificate_id) {
        failedDownloads.push(`${cert.name} (${cert.email}) - No certificate ID`);
        continue;
      }

      try {
        const result = await getServerRenderedCertificate(developer_certificate_id);
        if (!result?.blob || !result?.fileName) {
          failedDownloads.push(`${cert.name} (${cert.email}) - Certificate template not found`);
          continue;
        }

        saveAs(result.blob, result.fileName);
        successCount++;
        if (i < dataToDownload.length - 1) await new Promise((resolve) => setTimeout(resolve, 500));
      } catch (error) {
        console.error("Error downloading certificate for:", cert.name, error);
        failedDownloads.push(`${cert.name} (${cert.email}) - ${error.message}`);
      }
    }

    if (dataToDownload.length === 1) {
      alert(successCount === 1 ? "Certificate downloaded successfully!" : `Failed downloads (1):\n${failedDownloads[0]}`);
    } else {
      let message = `Download completed!\nSuccessfully downloaded: ${successCount} certificate(s)\n`;
      if (failedDownloads.length > 0) {
        message += `Failed downloads (${failedDownloads.length}):\n${failedDownloads.join('\n')}`;
      }
      alert(message);
    }
  };

  const transformApiData = (apiData) => {
    if (!Array.isArray(apiData)) return [];

    return apiData.map((item) => {
      let coursework = "";
      let environment = "";

      const certType = item.certificate_type || "";
      if (certType) {
        const environmentMatch = certType.match(/\(([^)]+)\)/);
        if (environmentMatch) {
          environment = environmentMatch[1].trim();
          coursework = certType.replace(/\s*\([^)]+\)\s*/, '').trim();
        } else {
          coursework = certType.trim();
          environment = "Unknown";
        }
      }

      let formattedDate = "";
      const dateValue = item.developer_certification_datetime || item.date_created || "";
      if (dateValue) {
        try {
          formattedDate = new Date(dateValue).toISOString().split('T')[0];
        } catch (error) {
          formattedDate = dateValue.split('T')[0] || dateValue.split(' ')[0] || dateValue;
        }
      }

      let status = "pending";
      const statusValue = item.developer_certification_status || DEVELOPER_CERTIFICATION_STATUS.Pending;
      if (statusValue === DEVELOPER_CERTIFICATION_STATUS.Certified) status = "certified";
      else if (statusValue === DEVELOPER_CERTIFICATION_STATUS.Released) status = "released";
      else if (statusValue === DEVELOPER_CERTIFICATION_STATUS.Expired) status = "expired";

      return {
        name: item.developer_name || "",
        email: item.developer_email || "",
        txn_id: item.txn_id || "",
        txn_retrieval_ref: item.txn_retrieval_ref || "",
        date: formattedDate,
        coursework: coursework,
        environment: environment,
        status: status,
        training_provider: item.training_provider_name || "",
        proficiency: item.certificate_proficiency_type || "",
        _original: item
      };
    });
  };

  const getAllCertifications = async (tab) => {
    if ((isCertificateAdmin || isNetsAdmin) && tab !== "Released") {
      console.warn(`Access denied: ${isCertificateAdmin ? 'Certificate' : 'NETS'} Admin can only view Released tab`);
      return;
    }

    try {
      setLoad(true);
      setApiError(null);

      const endDate = appliedFilters.endDate || lastDayOfMonthFormatted;
      const startDate = appliedFilters.startDate || firstDayOfMonth;

      let statusCode, currentPage;
      switch (tab) {
        case "Pending":
          statusCode = DEVELOPER_CERTIFICATION_STATUS.Pending;
          currentPage = pendingPage;
          break;
        case "Certified":
          statusCode = DEVELOPER_CERTIFICATION_STATUS.Certified;
          currentPage = certifiedPage;
          break;
        case "Released":
          statusCode = DEVELOPER_CERTIFICATION_STATUS.Released;
          currentPage = releasedPage;
          break;
      }

      let trainingProvider = "";
      if (appliedFilters.school !== "All") {
        const trainingProviderKey = Object.keys(TrainingProviderSyscodeString).find(
          key => TrainingProviderSyscodeString[key] === appliedFilters.school
        );
        if (trainingProviderKey) trainingProvider = trainingProviderKey;
      }

      const combinedSyscodes = getCombinedSyscodes(appliedFilters.coursework, appliedFilters.environment);
      let certificateSyscode = combinedSyscodes.length > 0 ? combinedSyscodes.join(',') : "";

      const getCertificationUrl = urls.resourceOwnerUrl +
        commonConfigs.apiUrls.getAllStudentCertificationDetails(
          startDate, endDate, certificateSyscode, statusCode, trainingProvider, currentPage, apiSearchTerm || ""
        );

      const response = await beveatApiResourceOwner.get(getCertificationUrl, {
        headers: commonConfigs.resourceOwnerApiHeader,
      });

      let certificatesArray = null;
      let totalPagesCount = 0;
      let totalRecordsCount = 0;

      if (response.data?.result?.data?.certificates && Array.isArray(response.data.result.data.certificates)) {
        certificatesArray = response.data.result.data.certificates;
        totalPagesCount = response.data.result.data.total_pages || 0;
        totalRecordsCount = response.data.result.data.total_records || 0;
      } else if (response.data?.result?.data && Array.isArray(response.data.result.data)) {
        certificatesArray = response.data.result.data;
      }

      const transformedData = certificatesArray && Array.isArray(certificatesArray) ? transformApiData(certificatesArray) : [];

      switch (tab) {
        case "Pending":
          setPendingData(transformedData);
          setTotalPendingPages(totalPagesCount);
          setTotalPendingRecords(totalRecordsCount);
          break;
        case "Certified":
          setCertifiedData(transformedData);
          setTotalCertifiedPages(totalPagesCount);
          setTotalCertifiedRecords(totalRecordsCount);
          break;
        case "Released":
          setReleasedData(transformedData);
          setTotalReleasedPages(totalPagesCount);
          setTotalReleasedRecords(totalRecordsCount);
          break;
      }
    } catch (error) {
      console.error("Error fetching certification data:", error);
      setApiError("Failed to load certification data. Please try again.");
      switch (tab) {
        case "Pending":
          setPendingData([]);
          setTotalPendingPages(0);
          setTotalPendingRecords(0);
          break;
        case "Certified":
          setCertifiedData([]);
          setTotalCertifiedPages(0);
          setTotalCertifiedRecords(0);
          break;
        case "Released":
          setReleasedData([]);
          setTotalReleasedPages(0);
          setTotalReleasedRecords(0);
          break;
      }
    } finally {
      setLoad(false);
    }
  };

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (availableTabs.includes("Pending")) getAllCertifications("Pending");
    if (availableTabs.includes("Certified")) getAllCertifications("Certified");
    if (availableTabs.includes("Released")) getAllCertifications("Released");
  }, [appliedFilters.startDate, appliedFilters.endDate, appliedFilters.school, appliedFilters.coursework, appliedFilters.environment, apiSearchTerm]);

  useEffect(() => {
    if (!load) getAllCertifications(activeTab);
  }, [pendingPage, certifiedPage, releasedPage]);

  const currentData = getCurrentData();

  useEffect(() => {
    setSelectedRows(new Set());
  }, [activeTab, apiSearchTerm]);

  const courseworkOptions = useMemo(() =>
    Object.keys(isNetsAdmin ? CertificateSyscodeByCourseworkNetsOnly : CertificateSyscodeByCoursework),
    [isNetsAdmin]
  );
  const environmentOptions = useMemo(() => ["Sandbox", "UAT", "Go Live"], []);

  const trainingProviderOptions = useMemo(() => {
    if (userTrainingProviderId) {
      return [userTrainingProviderName];
    }
    return ["All", ...Object.values(TrainingProviderSyscodeString).filter(value => value !== "Reserved").sort()];
  }, [userTrainingProviderId, userTrainingProviderName]);

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({ ...prev, [filterType]: value }));
  };

  const handleApply = () => {
    setAppliedFilters(filters);
    setPendingPage(0);
    setCertifiedPage(0);
    setReleasedPage(0);
  };

  const handleReset = () => {
    const resetFilters = {
      coursework: "All",
      environment: "All",
      school: userTrainingProviderName,
      startDate: firstDayOfMonth,
      endDate: lastDayOfMonthFormatted,
    };
    setFilters(resetFilters);
    setAppliedFilters(resetFilters);
    setPendingPage(0);
    setCertifiedPage(0);
    setReleasedPage(0);
    setApiSearchTerm("");
    setSearchTerm("");
  };

  const handleRowSelection = (rowId) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(rowId)) {
      newSelected.delete(rowId);
    } else {
      newSelected.add(rowId);
    }
    setSelectedRows(newSelected);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSelectedRows(new Set());
  };

  const handleDownloadData = async () => {
    try {
      let dataToDownload;
      if (selectedRows.size > 0) {
        dataToDownload = currentData.filter((item) => {
          const uniqueId = item._original?.developer_certificate_id || `${item.email}-${item.txn_id}`;
          return selectedRows.has(uniqueId);
        });
      } else {
        dataToDownload = await getAllCertificationsForDownload();
      }

      if (dataToDownload.length === 0) {
        alert("No data to download");
        return;
      }
      const workbook = new Excel.Workbook();
      const highlightColor = "92FFFF";
      const fillStyle = { type: "pattern", pattern: "solid", fgColor: { argb: highlightColor } };
      const worksheet = workbook.addWorksheet("Certification Data", { properties: { defaultColWidth: 20 } });

      worksheet.mergeCells("A1:G1");
      worksheet.getCell("A1").font = { size: 18, bold: true, underline: true };
      worksheet.getCell("A1").alignment = { vertical: "middle", horizontal: "center" };
      worksheet.getCell("A1").value = "Certification Name List";

      worksheet.getCell("G3").font = { size: 12, bold: true };
      worksheet.getCell("G3").value = `Status: ${activeTab}`;
      worksheet.getCell("G4").font = { size: 12, bold: true };
      worksheet.getCell("G4").value = `Start Date: ${appliedFilters.startDate || 'All'}`;
      worksheet.getCell("G5").font = { size: 12, bold: true };
      worksheet.getCell("G5").value = `End Date: ${appliedFilters.endDate || 'All'}`;
      worksheet.getCell("G6").font = { size: 12, bold: true };
      worksheet.getCell("G6").value = `Coursework: ${appliedFilters.coursework}`;
      worksheet.getCell("G7").font = { size: 12, bold: true };
      worksheet.getCell("G7").value = `Environment: ${appliedFilters.environment}`;
      worksheet.getCell("G8").font = { size: 12, bold: true };
      worksheet.getCell("G8").value = `Training Provider: ${appliedFilters.school}`;

      const headers = ["Name", "Email", "Transaction ID", "Transaction Retrieval Reference", "Date", "Coursework", "Environment"];
      if (activeTab === "Certified" || activeTab === "Released") headers.push("Proficiency");

      worksheet.getRow(12).font = { size: 12, bold: true };
      worksheet.getRow(12).alignment = { vertical: "middle", horizontal: "center", wrapText: true };
      worksheet.getRow(12).values = headers;
      for (let i = 1; i <= headers.length; i++) {
        worksheet.getRow(12).getCell(i).fill = fillStyle;
      }

      let row = 13;
      dataToDownload.forEach((item) => {
        const rowData = [item.name, item.email, item.txn_id, item.txn_retrieval_ref, item.date, item.coursework, item.environment];
        if (activeTab === "Certified" || activeTab === "Released") rowData.push(item.proficiency || "");

        worksheet.getRow(row).alignment = { vertical: "middle", horizontal: "center", wrapText: true };
        worksheet.getRow(row).font = { size: 11 };
        worksheet.getRow(row).values = rowData;

        for (let j = 1; j <= rowData.length; j++) {
          worksheet.getRow(row).getCell(j).border = {
            top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" }
          };
        }
        row++;
      });

      worksheet.getColumn(1).width = 25;
      worksheet.getColumn(2).width = 30;
      worksheet.getColumn(3).width = 40;
      worksheet.getColumn(4).width = 35;
      worksheet.getColumn(5).width = 12;
      worksheet.getColumn(6).width = 20;
      worksheet.getColumn(7).width = 15;
      if (activeTab === "Certified" || activeTab === "Released") worksheet.getColumn(8).width = 15;

      const summaryRow = row + 2;
      worksheet.getCell(`A${summaryRow}`).font = { size: 12, bold: true };
      worksheet.getCell(`A${summaryRow}`).value = `Total Records: ${dataToDownload.length}`;

      const currentDate = new Date().toISOString().split('T')[0];
      const filterSuffix = appliedFilters.coursework !== 'All' ? `_${appliedFilters.coursework.replace(/\s+/g, '_')}` : '';
      const searchSuffix = apiSearchTerm ? `_search_${apiSearchTerm.replace(/\s+/g, '_')}` : '';
      const selectionSuffix = selectedRows.size > 0 ? '_selected' : '_all';
      const fileName = `certification_data_${activeTab}_${currentDate}${filterSuffix}${searchSuffix}${selectionSuffix}.xlsx`;

      await workbook.xlsx.writeBuffer().then(data => {
        const blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });
        FileSaver.saveAs(blob, fileName);
      });
    } catch (error) {
      console.error("Error downloading data:", error);
      alert("Failed to download data. Please try again.");
    }
  };

  const columns = useMemo(() => {
    const baseColumns = [
      { Header: "Name", accessor: "name" },
      { Header: "Email", accessor: "email" },
      { Header: "txn_id", accessor: "txn_id" },
      { Header: "txn_retrieval_ref", accessor: "txn_retrieval_ref" },
      { Header: "Date", accessor: "date" },
      { Header: "Coursework", accessor: "coursework" },
      { Header: "Environment", accessor: "environment" },
    ];

    if (activeTab === "Certified" || activeTab === "Released") {
      baseColumns.push({ Header: "Proficiency", accessor: "proficiency" });
    }

    if (activeTab === "Pending") {
      baseColumns.push({
        id: "actions",
        Cell: ({ row }) => {
          const uniqueId = row.original._original?.developer_certificate_id || `${row.original.email}-${row.original.txn_id}`;
          return (
            <div className="text-gray-900 flex justify-end items-center gap-10" style={{ paddingRight: isMobile ? "8px" : "15px" }}>
              <button onClick={() => handleEditCertificate(row.original)} style={{ minHeight: isMobile ? "44px" : undefined, minWidth: isMobile ? "44px" : undefined, display: "flex", alignItems: "center", justifyContent: "center", padding: isMobile ? "8px" : undefined }} title="Edit certificate details">
                <Edit className={`${isMobile ? 'w-5 h-5' : 'w-6 h-6'}`} />
              </button>
              <button onClick={() => handleCertify(row.original)} style={{ minHeight: isMobile ? "44px" : undefined, minWidth: isMobile ? "44px" : undefined, display: "flex", alignItems: "center", justifyContent: "center", padding: isMobile ? "8px" : undefined }} title="Certify student">
                <MedalStar className={`${isMobile ? 'w-5 h-5' : 'w-6 h-6'}`} />
              </button>
              <input type="checkbox" checked={selectedRows.has(uniqueId)} onChange={() => handleRowSelection(uniqueId)} className="rounded border-2 border-black text-blue-600 focus:ring-blue-500" style={{ minHeight: isMobile ? "20px" : "20px", minWidth: isMobile ? "20px" : "20px" }} />
            </div>
          );
        },
      });
    } else if (activeTab === "Certified") {
      baseColumns.push({
        id: "actions",
        Cell: ({ row }) => {
          const uniqueId = row.original._original?.developer_certificate_id || `${row.original.email}-${row.original.txn_id}`;
          return (
            <div className="text-gray-900 flex justify-end items-center gap-10" style={{ paddingRight: isMobile ? "8px" : "15px" }}>
              <button onClick={() => handleEditCertificate(row.original)} style={{ minHeight: isMobile ? "44px" : undefined, minWidth: isMobile ? "44px" : undefined, display: "flex", alignItems: "center", justifyContent: "center", padding: isMobile ? "8px" : undefined }} title="Edit certificate details">
                <Edit className={`${isMobile ? 'w-5 h-5' : 'w-6 h-6'}`} />
              </button>
              <button onClick={() => handleOpenUploadModal(row.original)} style={{ minHeight: isMobile ? "44px" : undefined, minWidth: isMobile ? "44px" : undefined, display: "flex", alignItems: "center", justifyContent: "center", padding: isMobile ? "8px" : undefined }} title="Upload certificate">
                <DocumentUpload className={`${isMobile ? 'w-5 h-5' : 'w-6 h-6'}`} />
              </button>
              <input type="checkbox" checked={selectedRows.has(uniqueId)} onChange={() => handleRowSelection(uniqueId)} className="rounded border-2 border-black text-blue-600 focus:ring-blue-500" style={{ minHeight: isMobile ? "20px" : "20px", minWidth: isMobile ? "20px" : "20px" }} />
            </div>
          );
        },
      });
    } else if (activeTab === "Released") {
      baseColumns.push({
        id: "actions",
        Cell: ({ row }) => {
          const uniqueId = row.original._original?.developer_certificate_id || `${row.original.email}-${row.original.txn_id}`;
          return (
            <div className="text-gray-900 flex justify-end items-center" style={{ paddingRight: isMobile ? "8px" : "15px" }}>
              <input type="checkbox" checked={selectedRows.has(uniqueId)} onChange={() => handleRowSelection(uniqueId)} className="rounded border-2 border-black text-blue-600 focus:ring-blue-500" style={{ minHeight: isMobile ? "20px" : "20px", minWidth: isMobile ? "20px" : "20px" }} />
            </div>
          );
        },
      });
    }

    return baseColumns;
  }, [isMobile, selectedRows, activeTab]);

  if (load) {
    return (
      <div className="w-full bg-gray-100 min-h-screen">
        <div className="flex items-center justify-center" style={{ minHeight: "50vh" }}>
          <div className="text-center p-8">
            <div className={`animate-spin rounded-full border-4 border-blue-200 border-t-blue-600 mx-auto mb-4 ${isMobile ? 'h-12 w-12' : 'h-16 w-16'}`}></div>
            <h3 className={`font-medium text-gray-900 ${isMobile ? 'text-lg' : 'text-xl'}`}>Loading Certification Data</h3>
            <p className={`text-gray-600 ${isMobile ? 'text-sm' : 'text-base'}`}>Please wait while we fetch the certification data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (apiError) {
    return (
      <div className="w-full bg-gray-100 min-h-screen">
        <div className="flex items-center justify-center" style={{ minHeight: "50vh" }}>
          <div className="text-center p-8">
            <div className="text-red-500 text-6xl mb-4">⚠️</div>
            <h3 className={`font-medium text-red-800 ${isMobile ? 'text-lg' : 'text-xl'} mb-2`}>Error Loading Data</h3>
            <p className={`text-red-600 ${isMobile ? 'text-sm' : 'text-base'} mb-4`}>{apiError}</p>
            <button onClick={() => getAllCertifications(activeTab)} className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500">
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-gray-100 min-h-screen">
      {isMobile && (
        <style>
          {`
            @media (max-width: 768px) {
              .table-container .flex.justify-between.items-center {
                flex-direction: column !important;
                align-items: center !important;
                gap: 12px !important;
              }
              .certification-filters {
                flex-direction: column !important;
                gap: 16px !important;
              }
              .filter-buttons {
                flex-direction: column !important;
                gap: 12px !important;
              }
            }
          `}
        </style>
      )}

      <div className={`${isMobile ? 'p-4' : 'p-6'}`}>
        {isMobile && (
          <div className="text-center mb-4">
            <h2 className="font-bold text-gray-900" style={{ fontSize: "20px", marginBottom: "0", lineHeight: "1.3" }}>Certification</h2>
          </div>
        )}

        <AdminCertificationFilter
          filters={filters}
          courseworkOptions={courseworkOptions}
          environmentOptions={environmentOptions}
          trainingProviderOptions={trainingProviderOptions}
          isMobile={isMobile}
          handleFilterChange={handleFilterChange}
          handleApply={handleApply}
          handleReset={handleReset}
        />

        <div className="mb-6">
          <div className="border-b border-gray-200 flex justify-between items-center">
            <nav className="-mb-px flex space-x-8" aria-label="Tabs">
              {availableTabs.map((tab) => (
                <button key={tab} onClick={() => handleTabChange(tab)} className={`${activeTab === tab ? "border-blue-500 text-blue-600" : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"} whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm`} style={{ minHeight: isMobile ? "44px" : undefined, padding: isMobile ? "12px 16px" : undefined }}>
                  {tab}
                </button>
              ))}
            </nav>
            <div className="flex items-center mb-1">
              <label className="flex gap-x-2 items-center">
                <span className="text-gray-700 font-medium text-sm">Search: </span>
                <div className="relative flex items-center">
                  <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} onKeyPress={handleSearchKeyPress} className="px-3 py-2 pr-12 rounded-md border border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 w-80" placeholder={`Name, Email, Txn_id, Txn_retreival_ref...`} style={{ minHeight: isMobile ? "44px" : "40px", fontSize: "14px" }} />
                  <button type="button" className="absolute right-1 top-1/2 transform -translate-y-1/2 flex items-center justify-center p-2 bg-gray-200 text-gray-800 hover:text-gray-700 focus:outline-none rounded-md hover:bg-gray-100" onClick={handleAdvancedSearch} title="Search" style={{ minHeight: isMobile ? "36px" : "32px", minWidth: isMobile ? "36px" : "32px" }}>
                    <FilterSearch className="w-4 h-4" />
                  </button>
                </div>
              </label>
            </div>
          </div>
        </div>

        <AdminCertificationDetailsTable
          columns={columns}
          filteredData={currentData}
          isMobile={isMobile}
          handleDownloadData={handleDownloadData}
          handleDownloadCertificates={handleDownloadCertificates}
          selectedRows={selectedRows}
          handleRowSelection={handleRowSelection}
          enableDownloadCertificates={activeTab === "Certified"}
          pageChangeHandler={handlePageChange}
          totalData={getTotalPages()}
          currentPage={getCurrentPage()}
          totalRecords={getTotalRecords()}
        />

        {currentData.length === 0 && (
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="flex items-center justify-center py-16">
              <div className="text-center">
                <h3 className={`font-medium text-gray-700 ${isMobile ? 'text-lg' : 'text-xl'} mb-2`}>No Data Found</h3>
                <p className={`text-gray-500 ${isMobile ? 'text-sm' : 'text-base'} mb-4`}>
                  {apiSearchTerm ? `No certificates found for "${apiSearchTerm}" in the ${activeTab.toLowerCase()} status` : `No certificates found for the ${activeTab.toLowerCase()} status`}
                </p>
                {(appliedFilters.coursework !== "All" || appliedFilters.environment !== "All" || appliedFilters.school !== userTrainingProviderName || appliedFilters.startDate || appliedFilters.endDate || apiSearchTerm) && (
                  <button onClick={handleReset} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500">
                    Clear All Filters
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* <UpdateCertificateDetailsModal isOpen={isEditModalOpen} editFormData={editFormData} handleInputChange={handleInputChange} handleCloseModal={handleCloseModal} activeTab={activeTab} onUpdateSuccess={handleUpdateSuccess} /> */}
      <UpdateCertificateDetailsModal isOpen={isEditModalOpen} setOpenModal={setIsEditModalOpen} role="admin" activeTab={activeTab} data={editingCertificate} onUpdateSuccess={handleUpdateSuccess} />
      <ConfirmCertificationModal isOpen={isConfirmModalOpen} certifyingItem={certifyingItem} selectedProficiency={selectedProficiency} handleProficiencyChange={handleProficiencyChange} handleCancelCertification={handleCancelCertification} onConfirmSuccess={handleConfirmSuccess} />
      <UploadCertificateModal isOpen={isUploadModalOpen} uploadingCertificate={uploadingCertificate} handleCloseModal={handleCloseUploadModal} onUploadSuccess={handleUploadSuccess} />
    </div>
  );
};

export default AdminCertificationLayout;